"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { DetectionBox, HeroDetectionOverlay } from "@/lib/demo-finetunes";

interface DetectionOverlayProps {
  overlay: HeroDetectionOverlay;
}

function BoxRect({
  box,
  containerWidth,
  containerHeight,
  color,
  strokeWidth = 2.5,
  dashed = false,
}: {
  box: DetectionBox;
  containerWidth: number;
  containerHeight: number;
  color: string;
  strokeWidth?: number;
  dashed?: boolean;
}) {
  const x = box.xMin * containerWidth;
  const y = box.yMin * containerHeight;
  const w = (box.xMax - box.xMin) * containerWidth;
  const h = (box.yMax - box.yMin) * containerHeight;

  return (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeDasharray={dashed ? "6 3" : undefined}
      rx={2}
    />
  );
}

export function DetectionOverlay({ overlay }: DetectionOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    // Find the <img> sibling inside the same parent
    const img = el.parentElement?.querySelector("img");
    if (!img || !img.naturalWidth) return;

    const containerRect = el.parentElement!.getBoundingClientRect();
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerW / containerH;

    let renderW: number;
    let renderH: number;
    if (imgAspect > containerAspect) {
      // Image is wider than container -> letterboxed top/bottom
      renderW = containerW;
      renderH = containerW / imgAspect;
    } else {
      // Image is taller -> pillarboxed left/right
      renderH = containerH;
      renderW = containerH * imgAspect;
    }

    setSize({ width: renderW, height: renderH });
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    // Also measure after images load
    const img = containerRef.current?.parentElement?.querySelector("img");
    if (img) {
      img.addEventListener("load", measure);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (img) img.removeEventListener("load", measure);
    };
  }, [measure]);

  if (!size.width || !size.height) {
    return <div ref={containerRef} className="absolute inset-0" />;
  }

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      <svg
        width={size.width}
        height={size.height}
        viewBox={`0 0 ${size.width} ${size.height}`}
        className="pointer-events-none"
      >
        {/* Prediction boxes - green if correct, red if wrong */}
        {overlay.predictions.map((box, i) => (
          <BoxRect
            key={`pred-${i}`}
            box={box}
            containerWidth={size.width}
            containerHeight={size.height}
            color={box.correct === false ? "#ef4444" : "#22c55e"}
            strokeWidth={2.5}
          />
        ))}
      </svg>
    </div>
  );
}
